import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import Response = ZoningAPI.Response;
import IInstance = ZoningAPI.IInstance;

import * as _ from 'lodash';
import { Instance, InstanceFactory } from '../models/instance';
import { BaseEntityService } from 'src/app/shared/services/base-entity.service';
import { AuthenticationService, AuthenticationStatus } from 'src/app/shared/services/authentication.service';

const API_ENDPOINT = environment.ZONAR_API_URL + '/instance';

@Injectable({
    providedIn: 'root'
})
export class InstanceService extends BaseEntityService<Instance> {
    private readonly instancesSubject = new BehaviorSubject<Instance[]>([]);
    readonly instances = this.instancesSubject.asObservable().pipe(distinctUntilChanged());

    readonly currentInstanceSubject = new BehaviorSubject<Instance>(null);
    readonly currentInstance = this.currentInstanceSubject.asObservable().pipe(distinctUntilChanged());

    private requestedInstancesIds: number[] = [];

    get selectedInstance(): Instance {
        return this.currentInstanceSubject.value;
    }

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthenticationService,
        private readonly router: Router
    ) {
        super(InstanceFactory);

        this.authService.status
            .pipe(
                filter(status => status === AuthenticationStatus.LOGGED_IN),
                switchMap(() => this.requestInstances())
            )
            .subscribe(
                instances => this.instancesSubject.next(instances),
                err => this.instancesSubject.error(err)
            );

        combineLatest(
            this.instances.pipe(filter(instances => instances.length > 0)),
            this.router.events.pipe(
                filter(event => {
                    return event instanceof NavigationEnd && /\/instances\/instance\/\d+/.test(event.urlAfterRedirects);
                }),
                switchMap(() => {
                    const [, , , , , id] = window.location.href.split('/');

                    // @ts-ignore
                    return of(parseInt(id, 10));
                })
            )
        ).subscribe(([_instances, pickedInstanceId]) => {
            this.setInstanceById(pickedInstanceId);
        });
    }

    get hasSelectedInstance() {
        return !!this.selectedInstance;
    }

    private requestInstances(): Observable<Instance[]> {
        return this.http.get<Response>(API_ENDPOINT).pipe(
            map(response => this.initEntities(response.data)),
            map(entities => _.sortBy(entities, 'Name'))
        );
    }

    getInstanceById(instanceId: number) {
        return this.instancesSubject.value.find(instance => instance.Id === instanceId) || null;
    }

    setInstanceById(instanceId: number) {
        const instance = this.getInstanceById(instanceId);
        this.currentInstanceSubject.next(instance);
        instance
            ? this.router.navigate(['instances', 'instance', instance.Id], { replaceUrl: true })
            : this.router.navigate(['instances'], { replaceUrl: true });
    }

    getInstanceByState(state: string) {
        return this.instancesSubject.value.find(instance => instance.State === state) || null;
    }

    setInstanceByState(state: string) {
        this.currentInstanceSubject.next(this.getInstanceByState(state));
    }

    // TODO :: it is better to have mapper for that
    private updateCurrentInstanceData(updatedInstance: Instance) {
        if (!(updatedInstance instanceof Instance)) {
            return;
        }

        this.currentInstanceSubject.value.initWithJSON(updatedInstance);
    }

    updateInstance(id: number, updateStatistic: boolean = false): Observable<Instance> {
        const params = new HttpParams({
            fromObject: {
                updateStatistic: String(updateStatistic)
            }
        });
        return this.http
            .get<Response>(`${API_ENDPOINT}/${id}`, { params })
            .pipe(
                map(response => new Instance(response.data)),
                tap(instance => {
                    this.updateCurrentInstanceData(instance);
                })
            );
    }

    getUpdatedSelectedInstanceStatistic(updateStatistic: boolean = false) {
        if (!this.selectedInstance || this.requestedInstancesIds.indexOf(this.selectedInstance.Id) >= 0) {
            return;
        }

        const selectedInstanceId = this.selectedInstance.Id;
        this.requestedInstancesIds.push(selectedInstanceId);

        return this.updateInstance(selectedInstanceId, updateStatistic).pipe(
            map(() => {
                _.remove(this.requestedInstancesIds, instanceId => instanceId === selectedInstanceId);
            })
        );
    }

    updateSelectedInstanceStatistic(updateStatistic: boolean = false) {
        if (!this.selectedInstance || this.requestedInstancesIds.indexOf(this.selectedInstance.Id) >= 0) {
            return;
        }

        return this.getUpdatedSelectedInstanceStatistic(updateStatistic).subscribe();
    }

    clearCurrentInstance() {
        this.currentInstanceSubject.next(null);
    }

    updateInstanceFromJSON(instanceData: IInstance) {
        if (!instanceData) {
            return;
        }
        const instance = this.getInstanceById(instanceData.Id);
        if (!instance) {
            return;
        }

        instance.initWithJSON(instanceData);
    }
}
